/**
 * Listagem e gestão de tenants (super-admin).
 * Toda ação de mudança de plano requer justificativa obrigatória.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { Plan, TenantSummary } from '../../types/api'

const PLANS: Plan[] = ['GRATUITO', 'BÁSICO', 'PROFISSIONAL']

export function TenantsPage() {
  const qc = useQueryClient()
  const { data: tenants, isLoading, isError } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: adminApi.listTenants,
  })

  const [actionTenant, setActionTenant] = useState<TenantSummary | null>(null)
  const [newPlan, setNewPlan] = useState<Plan>('GRATUITO')
  const [justificativa, setJustificativa] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)

  const changePlan = useMutation({
    mutationFn: () => adminApi.changePlan(actionTenant!.id, newPlan, justificativa),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      setActionTenant(null)
      setJustificativa('')
      setActionError(null)
    },
    onError: (err) => setActionError(getErrorMessage(err)),
  })

  function openAction(tenant: TenantSummary) {
    setActionTenant(tenant)
    setNewPlan(tenant.plan)
    setJustificativa('')
    setActionError(null)
  }

  return (
    <div>
      <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>
        Tenants
      </h1>

      {/* Modal de ação */}
      {actionTenant && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setActionTenant(null) }}
        >
          <div className="card" style={{ width: '100%', maxWidth: '440px' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>
              Alterar plano — {actionTenant.name}
            </h2>
            {actionError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{actionError}</div>}

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, display: 'block', marginBottom: 'var(--space-1)' }}>
                Novo plano
              </label>
              <select className="input" value={newPlan} onChange={(e) => setNewPlan(e.target.value as Plan)}>
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, display: 'block', marginBottom: 'var(--space-1)' }}>
                Justificativa <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <textarea
                className="input"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={3}
                minLength={10}
                required
                placeholder="Obrigatório (mínimo 10 caracteres). Toda ação é auditada."
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                className="btn btn-primary"
                onClick={() => changePlan.mutate()}
                disabled={changePlan.isPending || justificativa.trim().length < 10}
              >
                {changePlan.isPending ? 'Salvando…' : 'Confirmar'}
              </button>
              <button className="btn btn-ghost" onClick={() => setActionTenant(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: isLoading || !tenants?.length ? 'var(--space-6)' : 0 }}>
        {isLoading ? (
          <PageLoading />
        ) : isError ? (
          <div className="alert alert-danger">Não foi possível carregar os tenants.</div>
        ) : !tenants?.length ? (
          <p style={{ textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
            Nenhum tenant cadastrado.
          </p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Plano</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td><span className="badge badge-blue">{t.plan}</span></td>
                    <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                      {new Date(t.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)' }}
                        onClick={() => openAction(t)}
                      >
                        Alterar plano
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
